import { createContext, useContext, useSyncExternalStore, useMemo } from "react";
import { createSchemaScope, humaniseLabel, type EditorState, type Node, type NodeEntry, type ReportTemplate } from "@broadpaper/core";
import type { LayoutResult } from "@broadpaper/renderer";
import type { EditorSession, UiState } from "./session.js";
import { useObservable } from "./observable.js";
import type { ReportDesignerOptions, FeatureFlags } from "../options.js";
import { DEFAULT_FEATURES } from "../options.js";

export const SessionContext = createContext<EditorSession | null>(null);

export function useSession(): EditorSession {
  const s = useContext(SessionContext);
  if (!s) throw new Error("BroadPaper components must be rendered inside a ReportDesigner");
  return s;
}

/** Subscribes to a slice of the editor store. Re-renders only when the slice changes (Object.is). */
export function useEditorState<S>(selector: (state: EditorState) => S): S {
  const session = useSession();
  return useSyncExternalStore(
    (cb) => session.store.subscribe(cb),
    () => selector(session.store.getState()),
    () => selector(session.store.getState())
  );
}

export function useTemplate(): ReportTemplate {
  return useEditorState((s) => s.template);
}

export function useSelection(): string[] {
  return useEditorState((s) => s.selection);
}

export function useMode(): "design" | "preview" {
  return useEditorState((s) => s.mode);
}

export function useNode(id: string | null | undefined): NodeEntry | undefined {
  const session = useSession();
  const version = useEditorState((s) => s.version);
  return useMemo(() => (id ? session.store.index().get(id) : undefined), [session, id, version]);
}

export function useOptions(): ReportDesignerOptions {
  const session = useSession();
  return useSyncExternalStore(
    (cb) => session.options.subscribe(cb),
    () => session.options.get(),
    () => session.options.get()
  );
}

export function useFeatures(): FeatureFlags {
  const o = useOptions();
  return useMemo(() => ({ ...DEFAULT_FEATURES, ...(o.features ?? {}) }), [o.features]);
}

export function useUi<S = UiState>(selector?: (u: UiState) => S): S {
  const session = useSession();
  return useObservable(session.ui, selector);
}

export function useLayout(): LayoutResult | null {
  const session = useSession();
  return useObservable(session.layout);
}

export function useLayoutVersion(): number {
  const session = useSession();
  return useObservable(session.layoutVersion);
}

export function useRegistryVersion(): number {
  const session = useSession();
  return useSyncExternalStore(
    (cb) => session.registry.subscribe(cb),
    () => session.registry.list().length,
    () => session.registry.list().length
  );
}

/**
 * What to call a node in the layer tree, the breadcrumb and the inspector.
 *
 * Blocks label themselves from their own props, which means a table announces
 * itself as "Table · portfolio.allocation" — the path the designer typed, not
 * the name the data source gives it. Running that through `humaniseLabel` turns
 * it into "Table · Portfolio › Asset allocation" using the same wording the
 * canvas already prints inside its token chips.
 */
export function nodeLabel(session: EditorSession, node: Node): string {
  const def = session.registry.get(node.type);
  if (node.name) return node.name;
  if (def?.canvas?.label) {
    try {
      const l = def.canvas.label({ ...(typeof def.defaultProps === "function" ? def.defaultProps() : def.defaultProps), ...node.props });
      if (l) return humaniseLabel(l, createSchemaScope(session.dataSources));
    } catch {
      /* ignore */
    }
  }
  return def?.name ?? node.type;
}

/**
 * The technical version of the same thing — block type, and the expressions it
 * is bound to — for tooltips and secondary lines. Readable names are the right
 * default; the paths are what you need the moment something is wrong.
 */
export function nodeDetail(session: EditorSession, node: Node): string {
  const def = session.registry.get(node.type);
  const props = { ...(typeof def?.defaultProps === "function" ? def.defaultProps() : def?.defaultProps), ...node.props } as Record<string, unknown>;
  const exprs = session.registry
    .bindingsOf(node.type)
    .map((b) => props[b.prop])
    .filter((v): v is string => typeof v === "string" && v.trim() !== "");
  return [def?.name ?? node.type, ...exprs].join(" · ");
}
